import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../components/api';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const reset_token = location.state?.reset_token;

    const [formData, setFormData] = useState({ new_password: '', confirm_password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!reset_token || !email) navigate('/mot-de-passe-oublie');
    }, [reset_token, email, navigate]);

    const getPasswordStrength = (password) => {
        if (!password) return { score: 0, label: '', color: '' };
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        const map = [
            { score: 0, label: '', color: '' },
            { score: 1, label: 'Faible', color: 'bg-red-500' },
            { score: 2, label: 'Moyen', color: 'bg-yellow-500' },
            { score: 3, label: 'Bon', color: 'bg-blue-500' },
            { score: 4, label: 'Excellent', color: 'bg-green-500' },
        ];
        return map[score] || map[0];
    };

    const strength = getPasswordStrength(formData.new_password);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.new_password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }
        if (formData.new_password !== formData.confirm_password) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await api.post('utilisateur/password-reset/reset/', {
                reset_token,
                new_password: formData.new_password,
                confirm_password: formData.confirm_password,
            });
            setSuccess(true);
            setTimeout(() => navigate('/connexion'), 2500);
        } catch (err) {
            setError(err.response?.data?.error || "Une erreur s'est produite. Veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4">
                <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl text-center">
                    <img className="mx-auto h-14 w-auto" src="/logo-anpe.png" alt="ANPE Logo" />
                    <div className="mt-6 flex justify-center">
                        <CheckCircle className="h-20 w-20 text-green-500" />
                    </div>
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Mot de passe modifié !</h2>
                    <p className="mt-2 text-gray-600">
                        Votre mot de passe a été réinitialisé avec succès.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">Redirection vers la connexion...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4">
            <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
                <div className="mb-6 text-center">
                    <img className="mx-auto h-14 w-auto" src="/logo-anpe.png" alt="ANPE Logo" />
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">Nouveau mot de passe</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Choisissez un nouveau mot de passe pour votre compte
                    </p>
                    <p className="text-sm font-medium text-orange-600">{email}</p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
                            Nouveau mot de passe <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="new_password"
                                name="new_password"
                                type={showPassword ? 'text' : 'password'}
                                className="appearance-none block w-full px-4 py-3 pl-12 pr-12 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                                placeholder="Minimum 8 caractères"
                                value={formData.new_password}
                                onChange={handleChange}
                            />
                            <button type="button" onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600">
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                        </div>
                        {formData.new_password && (
                            <div className="mt-2">
                                <div className="flex gap-1 mb-1">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= strength.score ? strength.color : 'bg-gray-200'}`} />
                                    ))}
                                </div>
                                {strength.label && (
                                    <p className="text-xs text-gray-500">Force : <span className="font-medium">{strength.label}</span></p>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
                            Confirmer le mot de passe <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="confirm_password"
                                name="confirm_password"
                                type={showConfirm ? 'text' : 'password'}
                                className={`appearance-none block w-full px-4 py-3 pl-12 pr-12 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm ${
                                    formData.confirm_password && formData.new_password !== formData.confirm_password
                                        ? 'border-red-400'
                                        : 'border-gray-300'
                                }`}
                                placeholder="Répétez le mot de passe"
                                value={formData.confirm_password}
                                onChange={handleChange}
                            />
                            <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600">
                                {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                        </div>
                        {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                            <p className="mt-1 text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                        )}
                        {formData.confirm_password && formData.new_password === formData.confirm_password && (
                            <p className="mt-1 text-xs text-green-600 flex items-center">
                                <CheckCircle className="h-3 w-3 mr-1" /> Les mots de passe correspondent
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                            loading ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-sm transition`}
                    >
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Réinitialisation...
                            </span>
                        ) : 'Réinitialiser le mot de passe'}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <Link to="/connexion" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Retour à la connexion
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
